import { Component, ReactNode } from 'react';

import { trackOutcome } from '@/apiCalls/uniqueIDTransferHelper';

export interface IErrorBoundaryProps {
    children: ReactNode;
}

interface IErrorBoundaryState {
    hasError: boolean;
}

export const testId = 'ErrorBoundary';

// A crash of the widget itself is an outage, not a drop-off: without this the
// session just stops and the applicant counts as a walk-away, which sends the
// fix to the wrong team. Individual service-call failures are deliberately not
// recorded here — serviceResult already carries those.
export class ErrorBoundary extends Component<
    IErrorBoundaryProps,
    IErrorBoundaryState
> {
    state: IErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(): IErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error): void {
        // The name and message identify the crash; the stack would blow the
        // reason field out and can hold URLs, so it stays behind.
        const reason = `${error?.name ?? 'Error'}: ${
            error?.message ?? 'unknown'
        }`.slice(0, 160);

        // trackOutcome flushes immediately, which matters here more than
        // anywhere: a crashed widget is about to lose its page. Guarded
        // because a throw inside componentDidCatch has no boundary left above
        // it — telemetry must never be able to worsen a crash.
        try {
            trackOutcome('systemFailed', reason);
        } catch {
            // Nothing to do: the crash is already recorded nowhere better.
        }
    }

    render(): ReactNode {
        // The tree below has already crashed; rendering nothing stops the
        // error from taking the host page's script down with it.
        if (this.state.hasError) return null;
        return this.props.children;
    }
}
